// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import {
  Stack,
  useMediaQuery,
  useTheme,
  TextField,
  Button,
  Chip,
  Popover,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {
  GridColDef,
  GridPaginationModel,
  GridRowId,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { DeleteConfirmModal, Table } from "../../molecules";
import {
  DeleteMenuRequest,
  IChangeRequest,
  useAuthorization,
  useBulkApproveChangeRequest,
  useDeleteMenu,
  useGetChangeRequestList,
} from "../../../hooks";
import { ApprovalBar, Loading } from "../../atoms";
import { useSetSnackBar } from "../../../store/Snackbar.state";
import { downloadExcel } from "../../../util/downloadExcel";
import { useUserInfo } from "../../../store/User.state";
import dayjs from "dayjs";
import screenNames from "../../../common/screenNames.json";

function RenderStatus(props: GridRenderCellParams<any, string>) {
  const { value } = props;
  return <>{value && <ApprovalBar status={value} />}</>;
}

export type TransactionApprovalListingTableProps = {
  onRowClick?: (data: { id: GridRowId; row: IChangeRequest }) => void;
  isMenuOpen?: boolean;
  setApprovedCounter?: (count: number) => void;
};

export const TransactionApprovalListingTable = ({
  onRowClick,
  isMenuOpen,
  setApprovedCounter,
}: TransactionApprovalListingTableProps) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const { showDelete, showUpdate } = useAuthorization();
  const setSnackbar = useSetSnackBar();
  const [userInfo] = useUserInfo();

  const [queryOptions, setQueryOptions] = useState({});
  const [tableData, setTableData] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: -1,
  });
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(0);
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [bulkUpdateResult, setBulkUpdateResult] = useState(null);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [totalAmount, setLocalTotalAmount] = useState<number | null>(null);

  const { data, mutate: getTransationList, isLoading } = useGetChangeRequestList();
  const { mutate: menuDelete, isLoading: isDeleteLoading } = useDeleteMenu();
  const { mutate: bulkApprove, isLoading: isBulkApproveLoading } = useBulkApproveChangeRequest();

  const hasLoading = isLoading || isDeleteLoading || isBulkApproveLoading;

  const resultRows = useMemo(() => {
    if (!bulkUpdateResult) return [];
    return (bulkUpdateResult as any).results.map((result: any) => {
      const originalRequest = tableData.find((d: any) => d.id === result.id);
      const change = result.definitionChange || result.financialChange || {};
      return {
        ...result,
        ...change,
        makerUserName: originalRequest ? originalRequest.makerUserName : (change.makerUserId || ''),
        resultStatus: result.status,
        resultMessage: result.message
      };
    });
  }, [bulkUpdateResult, tableData]);

  const resultColumns: GridColDef[] = [
    {
      field: "processGroup",
      headerName: "Ekran Adı",
      width: 200,
      valueGetter: (params) => screenNames[params.value] || params.value,
    },
    {
      field: "processType",
      headerName: "İşlem Tipi",
      width: 150,
      renderCell: (params) => translateOperationMode(params.value),
    },
    {
      field: "merchantName",
      headerName: "İşyeri Adı",
      width: 150,
      renderCell: (params) => {
        try {
          const parsedValue = JSON.parse(params.row.entityNewValue);
          return parsedValue[0].merchantName ?? "-";
        } catch {
          return "-";
        }
      },
    },
    {
      field: "paymentAmount",
      headerName: "Tutar",
      width: 150,
      renderCell: (params) => {
        try {
          const parsedValue = JSON.parse(params.row.entityNewValue);
          console.log('payment amount', parsedValue[0].paymentAmount ?? "0");
          return parsedValue[0].paymentAmount ?? "0" != null
            ? parseFloat(parsedValue[0].paymentAmount ?? "0").toLocaleString("tr-TR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + " TL"
            : "0.00 TL";
        } catch {
          return "-";
        }
      },
    },
    {
      field: "makerUserName",
      headerName: "Onaya Gönderen Kullanıcı",
      width: 200,
    },
    {
      field: "createDate",
      headerName: "Onaya Düşme Tarihi",
      width: 180,
      renderCell: (params) => {
        if (!params.value) return "";
        const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
        return formattedDate === "Invalid Date" ? "" : formattedDate;
      },
    },
    {
      field: "resultStatus",
      headerName: "Sonuç Durumu",
      width: 150,
    },
    {
      field: "resultMessage",
      headerName: "Sonuç Mesajı",
      flex: 1,
      minWidth: 200,
    },
  ];

  // ---------------- Fetching Data ----------------
  useEffect(() => {
    if (!isMenuOpen) {
      const requestPayload = {
        roleLevel: +userInfo?.level,
        makerUserId: +userInfo?.userId,
        status: "PENDING",
        ...getQueryParams(queryOptions),
      };
      if (queryOptions?.field && queryOptions?.value !== undefined) {
        requestPayload[queryOptions.field] = queryOptions.value;
      }

      getTransationList(requestPayload, {
        onSuccess: (data) => {
          if (data?.isSuccess) {
            setTableData(data?.data);
            setApprovedCounter?.(data?.data?.length || 0);
            setLocalTotalAmount(data?.data2 ?? null);
          } else {
            setSnackbar({
              severity: "error",
              description: data?.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      });
    }
  }, [
    getTransationList,
    paginationModel?.page,
    paginationModel?.pageSize,
    setSnackbar,
    queryOptions,
    shouldRefresh,
    isMenuOpen,
  ]);

  const handleChangePagination = (model: GridPaginationModel) => {
    setPaginationModel(model);
    setTableData(undefined);
    getTransationList(
      {
        roleLevel: +userInfo.level,
        makerUserId: +userInfo.userId,
        status: "PENDING",
      },
      {
        onSuccess: (data) => {
          if (data.isSuccess) {
            setTableData(data?.data);
          } else {
            setSnackbar({
              severity: "error",
              description: data.message,
              isOpen: true,
            });
          }
        },
        onError: () => {
          setSnackbar({
            severity: "error",
            description: "İşlem sırasında bir hata oluştu",
            isOpen: true,
          });
        },
      }
    );
  };

  // ---------------- Delete Handling ----------------
  const deleteRow = React.useCallback((menu: IChangeRequest) => () => {
    setIsDeleteModalOpen(true);
    setSelectedRowId(Number(menu.id));
  }, []);

  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleDeleteConfirm = () => {
    const deleteRequest: DeleteMenuRequest = { id: selectedRowId };
    menuDelete(deleteRequest, {
      onSuccess: (data: any) => {
        if (data.isSuccess) {
          setShouldRefresh((prev) => !prev);
          setSnackbar({
            severity: "success",
            isOpen: true,
            description: data.message,
          });
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });
    handleCloseDeleteModal();
  };

  const handleBulkApprove = () => {
    const selectedRows = tableData.filter((row) =>
      selectionModel.includes(row.id)
    );
    const { operationCode, operationMode, type } = selectedRows[0];
    bulkApprove({ id: selectionModel, approve: true, description: "", operationCode, operationMode, type: type }, {
      onSuccess: (data) => {
        if (data.data?.results) {
          setBulkUpdateResult(data.data);
          setIsResultModalOpen(true);
        } else {
          setSnackbar({ severity: 'success', description: data.message || 'İşlemler başarıyla onaylandı.', isOpen: true });
        }
        setShouldRefresh(prev => !prev);
        setSelectionModel([]);
      },
      onError: () => {
        setSnackbar({ severity: 'error', description: 'İşlem sırasında bir hata oluştu.', isOpen: true });
      }
    });
  };

  const handleBulkRejectClick = () => {
    setRejectionModalOpen(true);
  };

  const handleRejectionModalClose = () => {
    setRejectionModalOpen(false);
    setRejectionReason("");
  };

  const handleRejectionSubmit = () => {
    const selectedRows = tableData.filter((row) =>
      selectionModel.includes(row.id)
    );
    const { operationCode, operationMode, type } = selectedRows[0];
    bulkApprove({ id: selectionModel, approve: false, description: rejectionReason, operationCode, operationMode, type }, {
      onSuccess: (data) => {
        if (data.data?.results) {
          setBulkUpdateResult(data.data);
          setIsResultModalOpen(true);
        } else {
          setSnackbar({ severity: 'success', description: data.message || 'İşlemler başarıyla reddedildi.', isOpen: true });
        }
        setShouldRefresh(prev => !prev);
        setSelectionModel([]);
        handleRejectionModalClose();
      },
      onError: () => {
        setSnackbar({ severity: 'error', description: 'İşlem sırasında bir hata oluştu.', isOpen: true });
      }
    });
  };

  const selectedTotalAmount = useMemo(() => {
    if (selectionModel.length === 0) return null;
    const selectedRows = tableData.filter((row) => selectionModel.includes(row.id));
    let sum = 0;
    let hasPaymentAmount = false;
    for (const row of selectedRows) {
      if (row.entityNewValue) {
        try {
          const parsed = JSON.parse(row.entityNewValue);
          if (Array.isArray(parsed)) {
            for (const item of parsed) {
              if (typeof item.paymentAmount === 'number') {
                sum += item.paymentAmount;
                hasPaymentAmount = true;
              }
            }
          }
        } catch (_) {
          // ignore parse errors
        }
      }
    }
    return hasPaymentAmount ? sum : null;
  }, [selectionModel, tableData]);

  const displayTotalAmount = selectionModel.length > 0 ? selectedTotalAmount : totalAmount;

  const isBulkActionEnabled = useMemo(() => {
    if (selectionModel.length === 0) {
      return false;
    }
    const selectedRows = tableData.filter((row) =>
      selectionModel.includes(row.id)
    );
    if (selectedRows.length !== selectionModel.length) {
      return false;
    }
    if (selectedRows.length === 0) {
      return false;
    }
    const firstRow = selectedRows[0];
    return selectedRows.every(
      (row) => row.operationCode === firstRow.operationCode && row.operationMode === firstRow.operationMode
    );
  }, [selectionModel, tableData]);

  // ---------------- Helpers ----------------
  function translateOperationMode(mode: string) {
    switch (mode) {
      case "UPDATE":
        return "Güncelleme";
      case "DELETE":
        return "Silme";
      case "ADD":
        return "Tanımlama";
      case "SendMoney":
        return "Para Gönderme";
      default:
        return "Bilinmiyor";
    }
  }

  function transformStatus(mode: string) {
    switch (mode) {
      case "APPROVED":
        return "ONAYLANDI";
      case "REJECTED":
        return "REDDEDİLDİ";
      case "PENDING":
        return "ONAY BEKLİYOR";
      default:
        return "Bilinmiyor";
    }
  }

  const getQueryParams = (filters) => {
    const queryParams = {};
    for (let key in filters) {
      if (filters[key]?.toString()?.length >= 1) {
        queryParams[key] = filters[key];
      }
    }
    return queryParams;
  };

  const handleFilterChange = (filters) => {
    if (filters.clearFilter) {
      setQueryOptions({});
      return;
    }
    setQueryOptions(filters);
  };

  const onSave = () => {
    const requestPayload = {
      roleLevel: +userInfo.level,
      makerUserId: +userInfo.userId,
      status: "PENDING",
      ...getQueryParams(queryOptions),
    };

    getTransationList(requestPayload, {
      onSuccess: (data) => {
        if (data.isSuccess) {
          function fieldValue(data) {
            return data.map((item) => ({
              "Ekran Adı": screenNames[item?.operationCode] || item?.operationCode,
              "İşlem Tipi": translateOperationMode(item?.operationMode),
              "Onaya Gönderen Kullanıcı": item?.makerUserName,
              "Onaya Düşme Tarihi": dayjs(item?.createDate).format(
                "DD.MM.YYYY HH:mm"
              ),
              "Durum": transformStatus(item?.status),
            }));
          }
          const refactoredData = fieldValue(data?.data);
          downloadExcel(refactoredData || [], "Onaya Gelen İşlemler Listesi");
        } else {
          setSnackbar({
            severity: "error",
            description: data.message,
            isOpen: true,
          });
        }
      },
      onError: () => {
        setSnackbar({
          severity: "error",
          description: "İşlem sırasında bir hata oluştu",
          isOpen: true,
        });
      },
    });
  };

  // ---------------- Filter Toolbar ----------------
  const [localFilters, setLocalFilters] = useState(queryOptions);

  useEffect(() => {
    setLocalFilters(queryOptions);
  }, [queryOptions]);

  const applyFilters = () => {
    handleFilterChange(localFilters);
  };

  // ---------------- Columns ----------------
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "operationCode",
        headerName: "Ekran Adı",
        width: 300,
        valueGetter: (params) => screenNames[params.value] || params.value,
      },
      {
        field: "operationMode",
        headerName: "İşlem Tipi",
        width: 300,
        renderCell: (params) => translateOperationMode(params.value),
      },
      {
        field: "merchantName",
        headerName: "İşyeri Adı",
        width: 150,
        renderCell: (params) => {
          try {
            const parsedValue = JSON.parse(params.row.entityNewValue);
            return parsedValue[0].merchantName ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "paymentAmount",
        headerName: "Tutar",
        width: 150,
        renderCell: (params) => {
          try {
            const parsedValue = JSON.parse(params.row.entityNewValue);
            console.log('payment amount', parsedValue[0].paymentAmount ?? "0");
            return parsedValue[0].paymentAmount ?? "0" != null
              ? parseFloat(parsedValue[0].paymentAmount ?? "0").toLocaleString("tr-TR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) + " TL"
              : "0.00 TL";
          } catch {
            return "-";
          }
        },
      },
      {
        field: "makerUserName",
        headerName: "Onaya Gönderen Kullanıcı",
        width: 300,
      },
      {
        field: "createDate",
        headerName: "Onaya Düşme Tarihi",
        width: 300,
        renderCell: (params) => {
          if (!params.value) return "";
          const formattedDate = dayjs(params.value).format("DD.MM.YYYY HH:mm");
          return formattedDate === "Invalid Date" ? "" : formattedDate;
        },
      },
      {
        renderCell: RenderStatus,
        field: "status",
        headerName: "Durum",
        width: 300,
      },
    ],
    []
  );

  // ---------------- Render ----------------
  return (
    <>
      {hasLoading && <Loading />}

      <Stack flex={1} p={2} spacing={2}>
        {/* Toolbar */}
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FormControl size="small" sx={{ width: 300 }}>
              <InputLabel>Ekran Adı</InputLabel>
              <Select
                label="Ekran Adı"
                value={localFilters.operationCode || ""}
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    operationCode: e.target.value,
                  })
                }
              >
                <MenuItem value="">Tümü</MenuItem>
                {Object.entries(screenNames).map(([code, name]) => (
                  <MenuItem key={code} value={code}>
                    {name as string}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 300 }}>
              <InputLabel>İşlem Tipi</InputLabel>
              <Select
                label="İşlem Tipi"
                value={localFilters.operationMode || ""}
                onChange={(e) =>
                  setLocalFilters({
                    ...localFilters,
                    operationMode: e.target.value,
                  })
                }
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="ADD">Tanımlama</MenuItem>
                <MenuItem value="UPDATE">Güncelleme</MenuItem>
                <MenuItem value="DELETE">Silme</MenuItem>
                <MenuItem value="SendMoney">Para Gönderme</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" onClick={applyFilters}>
              Filtrele
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                handleFilterChange({ clearFilter: true });
              }}
            >
              Temizle
            </Button>
            <Button
              variant="contained"
              onClick={handleBulkApprove}
              disabled={!isBulkActionEnabled}
            >
              Toplu Onayla
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkRejectClick}
              disabled={!isBulkActionEnabled}
            >
              Toplu Reddet
            </Button>
          </Stack>
        </Stack>

        {displayTotalAmount !== null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 1,
              whiteSpace: 'nowrap',
              alignSelf: 'flex-start',
            }}
          >
            <Typography variant="caption" fontWeight={500} sx={{ opacity: 0.85 }}>
              Toplam Tutar
            </Typography>
            <Typography variant="body2" fontWeight={700}>
              {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(displayTotalAmount)}
            </Typography>
          </Box>
        )}

        {/* Table */}
        <Table
          columnVisibilityConfigKey="TransactionApprovalColumnVisibilityKey"
          hasActiveFilters={Object.keys(queryOptions).length > 0}
          handleFilterChange={handleFilterChange}
          paginationModel={paginationModel}
          onPaginationModelChange={handleChangePagination}
          paginationMode="server"
          rowCount={tableData?.length || 0}
          sx={{ width: isDesktop ? 1308 : window.innerWidth - 50 }}
          isRowSelectable={() => true}
          onRowSelectionModelChange={(newSelectionModel) =>
            setSelectionModel(newSelectionModel as GridRowId[])
          }
          rowSelectionModel={selectionModel}
          checkboxSelection
          onRowClick={!!showUpdate ? onRowClick : () => null}
          rows={tableData || []}
          className="myTable"
          customLabelDisplayedRows={true}
          columns={columns}
          exportFileName="Onaya Gelen İşlemler Listesi"
          onSave={() => onSave()}
        />

        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={() => handleDeleteConfirm()}
        />

        <Dialog open={rejectionModalOpen} onClose={handleRejectionModalClose} fullWidth maxWidth="sm">
          <DialogTitle>Toplu Reddet</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Reddetme Sebebi"
              type="text"
              fullWidth
              variant="outlined"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              multiline
              rows={4}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRejectionModalClose}>İptal</Button>
            <Button onClick={handleRejectionSubmit} variant="contained" color="primary" disabled={!rejectionReason.trim()}>
              Gönder
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={isResultModalOpen} onClose={() => setIsResultModalOpen(false)} fullWidth maxWidth="lg">
          <DialogTitle>Toplu İşlem Sonuçları</DialogTitle>
          <DialogContent>
            {bulkUpdateResult && (
              <Stack spacing={2}>
                <p>{(bulkUpdateResult as any).summaryMessage}</p>
                <Table
                  columns={resultColumns}
                  rows={resultRows}
                  hideFooter
                  autoHeight
                />
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsResultModalOpen(false)}>Kapat</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </>
  );
};
